import styled from '@emotion/styled';
import { colors } from '@kaizenplatform/kaizen-ui-colors/lib';
import _ from 'lodash';
import { createElement, FC } from 'react';

import {
  BusinessRegion,
  CreativeFormat,
  CreativeFormatAspectRatio,
  CreativeFormatPlaybackTime,
} from '../../../graphql/generated/graphql';
import { SingleAssignmentFragment } from '../../../graphql/generated/graphql_offer';
import { CreationByOptimizerId } from '../../../service/persistent/CreationSetWebApi';
import { getLatestModificationRequest } from '../../../utility/graphql/creation';
import CreationDetailLink from '../../component/compound/CreationDetailLink';
import CreationReward from '../../component/compound/CreationReward';
import CreationStateMessage from '../../component/compound/CreationStateMessage';
import ModificationRequestIssueIcon from '../../component/primitive/ModificationRequestIssueIcon';
import { SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import _CreativeFormatDisplay from './CreativeFormatDisplay';

export interface Props {
  creativeFormat: Pick<CreativeFormat, 'id' | 'offerPricingDetails' | 'resizeBaseId'> & {
    network: Pick<CreativeFormat['network'], 'key'>;
    placement: Pick<CreativeFormat['placement'], 'key' | 'name'>;
    creativeType: Pick<CreativeFormat['creativeType'], 'key'>;
    attribute: {
      playbackTime?: {
        seconds: CreativeFormatPlaybackTime['seconds'];
      } | null;
      aspectRatio?: Pick<CreativeFormatAspectRatio, 'aspectRatio' | 'size'> | null;
    };
  };
  creation: CreationByOptimizerId | undefined;
  assignment: SingleAssignmentFragment;
  organization: {
    businessRegion: Pick<BusinessRegion, 'key'>;
  };
  creationSetId: string;
  isResize?: boolean;
  expressDueDays?: number;
  className?: string;
}

const CreationListItem: FC<Props> = ({
  creativeFormat,
  creation,
  assignment,
  organization,
  creationSetId,
  isResize = false,
  expressDueDays = 0,
  className,
}) => {
  const rewardEnabled = organization.businessRegion.key !== 'gl' && creativeFormat.offerPricingDetails !== undefined;

  const RootComponent = generateRootComponent(rewardEnabled);
  const latestModificationRequest = creation
    ? getLatestModificationRequest(creation.deliverables, creation.modificationRequests)
    : null;

  const unresolvedIssueCount = latestModificationRequest
    ? latestModificationRequest.items.filter((item) => item?.issue && !item?.issue.resolved).length
    : 0;

  const backgroundColor =
    creation?.state === 'inspection_modification_requested' ||
    (latestModificationRequest && latestModificationRequest.state !== 'given_feedback')
      ? '#fff5c9'
      : isResize
      ? 'none'
      : colors.mochi__f2f6fa;

  return (
    <RootComponent
      className={className}
      style={{
        background: backgroundColor,
      }}
    >
      <CreativeFormatDisplay creativeFormat={creativeFormat} />
      <StateMessageWithIcon>
        <StyledCreationStateMessage
          creationState={creation?.state ? creation?.state : null}
          latestDeliverableModificationRequest={latestModificationRequest ? latestModificationRequest : null}
        />
        {unresolvedIssueCount > 0 ? (
          <ModificationRequestIssueIconWithBadge>
            <ModificationRequestIssueIcon unresolvedIssueCount={unresolvedIssueCount} />
          </ModificationRequestIssueIconWithBadge>
        ) : null}
      </StateMessageWithIcon>
      <StyledCreationReward
        offerPricingDetails={creativeFormat.offerPricingDetails ? creativeFormat.offerPricingDetails : null}
        modificationRequests={creation?.modificationRequests ? creation?.modificationRequests : null}
        expressDueDays={expressDueDays}
        rewardEnabled={rewardEnabled}
      />
      <StyledCreationDetailLink
        creativeFormat={creativeFormat}
        assignment={assignment}
        creation={creation}
        creationSetId={creationSetId}
      />
    </RootComponent>
  );
};

function generateRootComponent(rewardEnabled: boolean) {
  const columns = rewardEnabled ? ['45%', '27%', '16%', '12%'] : ['48%', '40%', '12%'];
  const areas = rewardEnabled
    ? [['creative_format', 'state', 'reward', 'detail_link']]
    : [['creative_format', 'state', 'detail_link']];

  return styled('div')(
    gridLayoutContainer({
      columns: columns,
      rows: ['auto'],
      areas: areas,
    }),
    {
      padding: `${SPACING_UNIT}px ${SPACING_REGULAR}px`,
      textDecoration: 'none',
      [':hover']: {
        textDecoration: 'none',
      },
    },
  );
}

const CreativeFormatDisplay = styled(_CreativeFormatDisplay)(gridLayoutItem('creative_format'));

const StateMessageWithIcon = styled('div')(
  gridLayoutItem('state'),
  flexibleLayoutContainer({ direction: 'row', alignItems: 'center' }),
  { marginRight: 10 },
);

const StyledCreationStateMessage = styled(CreationStateMessage)(
  flexibleLayoutContainer({ direction: 'row', alignItems: 'center' }),
);

const ModificationRequestIssueIconWithBadge = styled('div')({
  marginLeft: 5,
});

const StyledCreationReward = styled(CreationReward)(
  gridLayoutItem('reward'),
  flexibleLayoutContainer({ direction: 'row', alignItems: 'center' }),
);

const StyledCreationDetailLink = styled(CreationDetailLink)(
  gridLayoutItem('detail_link'),
  flexibleLayoutContainer({ direction: 'row', alignItems: 'center' }),
);

export default CreationListItem;
